import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Footer } from '../../../components/footers/Footer';
import { HomeHeader } from '../../../components/new/headers/HomeHeader';
import { getPodcastDetail } from '../api/api';
import { getMediaDisplaySrc, getPodcastGraphic2Url, resolveContentMedia } from '../helper/graphic2';
import { PageMain } from '../../../components/seo/PageMain';
import { DetailPageHelmet } from '../helper/DetailPageHelmet';
import { getPublicSiteUrl } from '../siteUrl';
import { documentTitle, metaDescriptionFromRecord } from '../helper/detailSeo';

const PODCAST_RICH_HTML_CLASS =
  [
    'new-podcast-content w-full max-w-none prose prose-base md:prose-lg',
    '[&_form]:mx-auto [&_form]:mt-6 [&_form]:w-full [&_form]:max-w-[760px] [&_form]:rounded-2xl [&_form]:border [&_form]:border-gray-200 [&_form]:bg-white [&_form]:p-6 [&_form]:shadow-[0_12px_30px_rgba(2,23,68,0.06)]',
    '[&_form]:max-md:max-w-full [&_form]:max-md:p-4',
    '[&_form_h2]:mt-0 [&_form_h3]:mt-0 [&_form_p]:mt-0 [&_form_h2]:mb-[0.85rem] [&_form_h3]:mb-[0.85rem] [&_form_p]:mb-[0.85rem]',
    '[&_form>div]:mt-4',
    '[&_form>div:first-of-type]:grid [&_form>div:first-of-type]:grid-cols-2 [&_form>div:first-of-type]:items-start [&_form>div:first-of-type]:gap-x-4 [&_form>div:first-of-type]:gap-y-[1.15rem] [&_form>div:first-of-type]:max-md:grid-cols-1',
    '[&_form_.grid]:grid [&_form_.grid]:grid-cols-2 [&_form_.grid]:items-start [&_form_.grid]:gap-x-4 [&_form_.grid]:gap-y-[1.15rem] [&_form_.grid]:max-md:grid-cols-1',
    '[&_form_label]:mb-1.5 [&_form_label]:block [&_form_label]:text-[0.95rem] [&_form_label]:font-medium [&_form_label]:text-[#021744]',
    "[&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:box-border [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:w-full [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:min-h-12 [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:rounded-[10px] [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:border [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:border-gray-300 [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:bg-white [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:px-[0.9rem] [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:py-3 [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:text-[#021744] [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select)]:outline-none",
    "[&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select):focus]:border-[#22c0b1] [&_form_:is(input[type='text'],input[type='tel'],input[type='email'],textarea,select):focus]:shadow-[0_0_0_3px_rgba(34,192,177,0.16)]",
    '[&_form_textarea]:min-h-[120px] [&_form_textarea]:resize-y',
    "[&_form_input[type='checkbox']]:mr-2 [&_form_input[type='checkbox']]:h-4 [&_form_input[type='checkbox']]:w-4 [&_form_input[type='checkbox']]:flex-shrink-0 [&_form_input[type='checkbox']]:accent-[#22c0b1]",
    "[&_form_input[type='checkbox']+span]:flex [&_form_input[type='checkbox']+span]:items-start [&_form_input[type='checkbox']+span]:gap-1.5 [&_form_input[type='checkbox']+span]:leading-[1.45]",
    "[&_form_label:has(input[type='checkbox'])]:flex [&_form_label:has(input[type='checkbox'])]:items-start [&_form_label:has(input[type='checkbox'])]:gap-1.5 [&_form_label:has(input[type='checkbox'])]:leading-[1.45]",
    '[&_form_.flex.flex-col]:gap-2',
    "[&_form_:is(button,input[type='submit'])]:inline-flex [&_form_:is(button,input[type='submit'])]:min-h-11 [&_form_:is(button,input[type='submit'])]:cursor-pointer [&_form_:is(button,input[type='submit'])]:items-center [&_form_:is(button,input[type='submit'])]:justify-center [&_form_:is(button,input[type='submit'])]:rounded-full [&_form_:is(button,input[type='submit'])]:border-0 [&_form_:is(button,input[type='submit'])]:bg-[#22c0b1] [&_form_:is(button,input[type='submit'])]:px-[1.4rem] [&_form_:is(button,input[type='submit'])]:py-[0.7rem] [&_form_:is(button,input[type='submit'])]:text-base [&_form_:is(button,input[type='submit'])]:font-semibold [&_form_:is(button,input[type='submit'])]:leading-none [&_form_:is(button,input[type='submit'])]:text-white",
    "[&_form_button:hover]:bg-[#021744] [&_form_input[type='submit']:hover]:bg-[#021744]",
  ].join(' ');

function formatPodcastDate(dateValue) {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return date.toDateString();
}

function mapPodcastRow(podcastRes) {
  return {
    postId: podcastRes.Id || podcastRes.id,
    postType: podcastRes.ContentType || podcastRes.Content_Type,
    title: podcastRes.Title,
    seoTitle: podcastRes.SeoTitle,
    metaDescription: podcastRes.MetaDescription,
    postDate: podcastRes.DatePublished || podcastRes.DateWritten || podcastRes.LastUpdated,
    postMedia: resolveContentMedia(podcastRes, getPodcastGraphic2Url),
    postName: podcastRes.Relevance,
    creator: podcastRes.Author,
    description: podcastRes.Description,
    body: podcastRes.Body || podcastRes.Content || podcastRes.content || podcastRes.Description,
  };
}

export function PodcastDetail() {
  const { podcastName } = useParams();
  const [podcastData, setPodcastData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const siteUrl = getPublicSiteUrl();
  const canonicalUrl = useMemo(() => {
    if (!siteUrl || !podcastName) return '';
    return `${siteUrl}/articles/podcast/${encodeURIComponent(podcastName)}`;
  }, [siteUrl, podcastName]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setPodcastData(null);
    setLoading(true);
    const name = podcastName;
    (async () => {
      if (!name) {
        if (!cancelled) {
          setLoadError('Missing podcast');
          setLoading(false);
        }
        return;
      }
      try {
        const podcastRes = await getPodcastDetail(name);
        if (!cancelled) {
          setPodcastData(mapPodcastRow(podcastRes));
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError('Podcast not found');
          setPodcastData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [podcastName]);

  const metaDescription = useMemo(() => {
    if (!podcastData) return 'Charlee.ai podcasts';
    return metaDescriptionFromRecord({
      metaDescription: podcastData.metaDescription,
      description: podcastData.description,
      body: podcastData.body,
      title: podcastData.title,
      fallback: 'Charlee.ai podcast',
    });
  }, [podcastData]);

  const ogImage = useMemo(() => {
    return getMediaDisplaySrc(podcastData?.postMedia);
  }, [podcastData]);

  const pageTitle = podcastData
    ? documentTitle(podcastData.seoTitle, podcastData.title, 'Podcast')
    : 'Podcast | Charlee.ai';
  const publishedDate = formatPodcastDate(podcastData?.postDate);
  const hasHtmlBody = /<\/?[a-z][\s\S]*>/i.test(podcastData?.body || '');

  const breadcrumbItems = useMemo(() => {
    if (!podcastData?.title) return undefined;
    return [
      { name: 'Home', path: '/' },
      { name: 'Podcasts', path: '/articles/podcasts' },
      { name: podcastData.title, url: canonicalUrl },
    ];
  }, [podcastData, canonicalUrl]);

  return (
    <div className="w-full overflow-hidden bg-[#F3F4F4]">
      {!loading && (
        <DetailPageHelmet
          pageTitle={pageTitle}
          metaDescription={metaDescription}
          canonicalUrl={canonicalUrl}
          ogImage={ogImage}
          ogType="article"
          noIndex={Boolean(loadError)}
          articlePublishedTime={podcastData?.postDate}
          author={podcastData?.creator}
          breadcrumbItems={breadcrumbItems}
          seoReady={!loading}
        />
      )}

      <HomeHeader headerColor="#F9F9F9" textColor="#021744" />
      <PageMain className="news-page bg-[#F9F9F9]">
        {loading && (
          <div
            className="mt-16 flex min-h-[50vh] w-full items-center justify-center md:mt-24"
            role="status"
            aria-label="Loading"
          >
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#22c0b1] border-t-transparent" />
          </div>
        )}

        {!loading && loadError && (
          <div className="mt-20 w-full px-4 text-center md:mt-32">
            <p className="text-lg text-[#021744]">{loadError}</p>
            <Link to="/articles/podcasts" className="mt-4 inline-block text-[#22c0b1] underline">
              Back to podcasts
            </Link>
          </div>
        )}

        {!loading && podcastData && (
          <>
            <article className="mt-16 w-full md:mt-24">
              <div className="mx-auto w-full max-w-4xl px-4 sm:px-6">
                <div className="w-full pt-6 md:pt-14">
                  <h1 className="text-[28px] font-medium leading-[1.15] tracking-normal sm:text-[36px] sm:leading-[39.6px] md:text-[65px] md:leading-[71.5px]">
                    {podcastData.title}
                  </h1>
                  <div className="flex w-full flex-wrap items-center justify-center gap-y-1 py-3 text-center text-sm text-[#0099B0] sm:gap-0 sm:py-2 sm:text-base">
                    <span className="px-2 sm:px-3">3 minute read</span>
                    {publishedDate ? (
                      <span className="border-l border-l-[#0099B0] px-2 sm:px-3">{publishedDate}</span>
                    ) : null}
                    <span className="border-l border-l-[#0099B0] px-2 sm:px-3">By Charlee AI</span>
                  </div>
                  <div className="w-full rounded-xl py-6 md:py-10">
                    <div className="w-full space-y-6 text-left md:space-y-8">
                      {ogImage ? (
                        <div className="w-full min-h-[220px] rounded-xl sm:min-h-[300px] md:min-h-[400px] lg:min-h-[500px]">
                          <img
                            src={ogImage}
                            alt={podcastData.title ? `${podcastData.title} — Charlee.ai podcast` : 'Charlee.ai podcast'}
                            className="h-full min-h-[220px] w-full rounded-xl object-cover sm:min-h-[300px] md:min-h-[400px] lg:min-h-[500px]"
                            crossOrigin="anonymous"
                          />
                        </div>
                      ) : null}
                      {hasHtmlBody ? (
                        <div
                          className={PODCAST_RICH_HTML_CLASS}
                          dangerouslySetInnerHTML={{ __html: podcastData.body }}
                        />
                      ) : (
                        <p className="new-podcast-content mt-4 whitespace-pre-line text-base leading-7 text-[#021744] md:text-[18px] md:leading-[30px]">
                          {podcastData.body}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </article>
            <div className="w-full bg-[#F3F4F4] py-12 md:py-20">
              <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
                <div className="flex w-full justify-start md:justify-end">
                  <Link to="/articles/podcasts" className="w-full sm:w-auto">
                    <button
                      type="button"
                      className="w-full rounded-full border border-[#021744] px-6 py-3 text-base font-medium leading-6 hover:bg-[#021744] hover:text-white sm:w-auto md:px-8 md:py-4 md:text-[18px]"
                    >
                      View all Podcasts
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </PageMain>
      <Footer />
    </div>
  );
}
